import {
  Icon,
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from '@hurdl/design-system';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import {
  ClipboardList,
  House,
  Menu,
  Settings,
  UtensilsCrossed,
  Users,
} from 'lucide-react';
import { type ReactElement, type ReactNode } from 'react';

import { Logo } from './brand/logo.js';
import { FAST_FOOD_PRODUCT_KEY } from './pages/menus-wiring.js';
import { ToastHost } from './pages/toast-context.js';
import { NAVIGATION } from './products.js';

/**
 * The app shell every page renders inside — a full-height navigation beside the `main`
 * landmark, and the toast host that reports an outcome (admin-panel R3).
 *
 * ── The frame ───────────────────────────────────────────────────────────────
 *
 * The shell owns every edge. `main.tsx` renders the `ThemeSurface` full-bleed and
 * `h-screen overflow-hidden`, so the surface is the viewport exactly, with no padding
 * ringing it and no page-level scrollbar in either Theme_Mode. The shell beneath it is
 * a single flex row filling that frame: the navigation against the start edge, and the
 * content column — a sticky header over a scrolling `main` — taking the rest. The
 * header is flush to the top of the content and the navigation flush to the start, so
 * nothing floats inset and there is no gutter to see.
 *
 * ── Mobile-first ────────────────────────────────────────────────────────────
 *
 * At `md` and up the navigation is a full-height rail against the start edge and the
 * header sits atop the content column. Below `md` the rail gives way to a drawer (the
 * Design_System `Sidebar`'s own `Sheet`, sliding in from the left) and the header spans
 * the full width carrying the trigger that opens it, so the chrome is reachable from any
 * page on a phone. Choosing a destination closes the drawer ({@link PanelNav}), so the
 * Operator is not left dismissing it by hand.
 *
 * {@link ToastHost} wraps the whole chrome, inside the application's `ThemeSurface` (in
 * `main.tsx`), so a toast resolves the theme and the pages beneath it raise reports
 * through `useToast`. The Design_System renders no page landmark, so the `main` is the
 * application's; each page supplies its own `h1`. The navigation holds no address of its
 * own — its links resolve against the route tree.
 */
export function Layout(): ReactElement {
  // The login and first-key enrol routes render outside the shell: a person who is not
  // signed in has no navigation to offer, so the Sole_Factor_Login page (R9.3) and the
  // enrol page (ADR 0050, which precedes the first sign-in) each own the whole themed
  // surface with no rail and no app bar. Every other route renders inside the shell below.
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });

  if (pathname === '/login' || pathname === '/enrol') {
    return (
      <ToastHost>
        <Outlet />
      </ToastHost>
    );
  }

  return (
    <ToastHost>
      {/*
        The shell pins itself to the viewport with `fixed inset-0`, so it owns every
        edge whatever wraps it. This is load-bearing: the themed surface the shell
        renders inside carries a comfortable page padding and only a `min-height`
        (`min-h-screen`), which it must — it is a *page* surface, and in the Cosmos
        playground it is the decorator's, not `main.tsx`'s, so the shell cannot assume
        the surface was sized or un-padded for it. `fixed inset-0` ignores that padding
        and gives the shell a definite viewport box, which is what makes the sidebar
        actually full-height (a flex child stretches to a definite parent, never to a
        `min-height` one) and leaves no gutter — in the real app and in Cosmos alike.
        `overflow-hidden` keeps the frame fixed; the content column inside does the
        scrolling. `panel-wash` paints the viewport in the panel's themed ambient
        gradient so the fixed layer never reveals the surface's padding behind it, and
        so every glass surface above it has colour to refract (the depth glassmorphism
        depends on). `overflow-x-hidden` on the `main` is the belt to the sidebar's
        braces: nothing in a page can introduce a horizontal page scrollbar.
      */}
      <div className="panel-wash fixed inset-0 flex overflow-hidden">
        <SidebarProvider className="h-full min-h-0">
          <PanelNav />
          <div className="flex min-w-0 grow flex-col overflow-hidden">
            <AppBar />
            <main className="min-h-0 grow overflow-x-hidden overflow-y-auto">
              <PageTransition>
                <div className="mx-auto w-full max-w-5xl p-hurdl-4 md:p-hurdl-6">
                  <Outlet />
                </div>
              </PageTransition>
            </main>
          </div>
        </SidebarProvider>
      </div>
    </ToastHost>
  );
}

/**
 * The page-change transition: a gentle rise-and-fade re-run on every navigation.
 *
 * Keyed on the pathname, so React replaces the wrapper on each route change and the
 * entrance plays afresh — one consistent motion between pages, rather than each page
 * animating itself (the pages no longer carry their own entrance).
 *
 * The entrance is a *fade*, not a rise, and the distinction is load-bearing: a rise
 * animates `translateY` and, under its `both` fill, leaves a `transform` on the wrapper
 * after it settles — and a transformed ancestor becomes the containing block for any
 * `position: sticky` inside it, which would quietly un-stick the review/edit page's
 * sticky header (the exact regression fixed in `review-editor.tsx`). A fade touches only
 * opacity, so the wrapper never becomes a containing block and the sticky header keeps
 * following. It is a platform entrance class (ADR 0044), so it resolves to no motion
 * under `prefers-reduced-motion` through the global rule in `tokens.css`.
 */
function PageTransition({
  children,
}: {
  readonly children: ReactNode;
}): ReactElement {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });

  return (
    <div key={pathname} className="hurdl-animate-fade-in">
      {children}
    </div>
  );
}

/**
 * The content column's header: on a phone the drawer trigger and the wordmark; on a
 * wide screen a slim bar the page title scrolls up to.
 *
 * It is `sticky top-0` within the scrolling `main`'s column so it stays present as the
 * page scrolls, and glass — the `panel-chrome` material, a translucent fill over a
 * backdrop blur — so the content beneath passes under it rather than being cut off by an
 * opaque strip, the deference a recessive chrome asks for. A single hairline `border-b`
 * separates it from the content. The wordmark shows only below `md`, where the rail
 * (which carries it in its header) is a drawer; at `md` and up the bar carries no mark,
 * because the rail already does, and sits as the quiet top edge the content meets.
 */
function AppBar(): ReactElement {
  return (
    <header className="panel-chrome sticky top-0 z-30 flex h-14 w-full shrink-0 items-center gap-hurdl-3 border-b border-border px-hurdl-4 text-card-foreground md:px-hurdl-6">
      <div className="md:hidden">
        <SidebarTrigger aria-label="Open navigation">
          <Icon icon={Menu} label="Open navigation" />
        </SidebarTrigger>
      </div>
      <Link
        to="/"
        aria-label="Hurdl Admin panel, home"
        className="flex md:hidden"
      >
        <Logo className="h-6 text-card-foreground" />
      </Link>
    </header>
  );
}

/**
 * The panel's navigation: the Hurdl wordmark, then two groups.
 *
 * 1. **Applications** — one entry per product. The current application (the fast-food
 *    Product) is expanded inline to its first-class pages — the Review queue, the Menus
 *    and the Categories — each an {@link Icon} and a typed `Link`, nested under the
 *    application. The same tree is the full-height rail on a wide screen and the drawer's
 *    content on a phone, no separate mobile view.
 * 2. **Admin** — the panel's own administration, distinct from any application: Users
 *    and Settings.
 *
 * The wordmark lives in the sidebar header, so the navigation names itself at its top —
 * the one place the mark belongs on a wide screen, where the full-height rail is the
 * frame's left edge. Each item is a {@link SidebarMenuButton} slotting a TanStack `Link`,
 * marked active where the current pathname is beneath it, so the navigation's addresses
 * stay the application's. On a phone, choosing a link closes the drawer: {@link
 * useSidebar} publishes the drawer's open state, and a navigation on a narrow viewport
 * sets it closed — the Operator picked a destination, so the drawer has done its job.
 */
function PanelNav(): ReactElement {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });

  const { isMobile, setOpenMobile } = useSidebar();

  const fastFood = NAVIGATION.find(
    (entry) => entry.productKey === FAST_FOOD_PRODUCT_KEY,
  );

  const base = `/products/${FAST_FOOD_PRODUCT_KEY}`;

  /** Closes the drawer after a navigation on a phone; a no-op on a wide screen. */
  function dismissOnMobile(): void {
    if (isMobile) {
      setOpenMobile(false);
    }
  }

  return (
    // `panel-chrome` is the rail's heavier glass; `border-0 border-r border-border`
    // replaces the column's default full border with a single inner divider, because the
    // rail is flush to the viewport's start edge and the outer three sides of a full
    // border would draw hairlines against the viewport itself (the frame the panel
    // removes). These are applied unconditionally rather than behind an `md:` variant:
    // `panel-chrome` is a plain CSS class, not a Tailwind utility, so `md:panel-chrome`
    // generates no rule — the fault that left the rail white. On a phone the same `nav`
    // renders inside the Sheet, where its chrome and divider would double the drawer's
    // own glass; `theme.css` neutralises the inner nav inside `[data-slot="sidebar-mobile"]`
    // so only the drawer panel's full-bleed glass shows. This is a shell decision the
    // application makes over the Primitive's default, which the Design_System's
    // single-side-border constraint (R10.5, its own sources only) does not reach.
    <Sidebar className="panel-chrome overflow-x-hidden border-0 border-r border-border">
      <SidebarHeader>
        <Link
          to="/"
          aria-label="Hurdl Admin panel, home"
          className="flex"
          onClick={dismissOnMobile}
        >
          <Logo className="h-6 text-card-foreground" />
        </Link>
      </SidebarHeader>
      <SidebarContent>
        <SidebarMenu>
          {/* Applications group. */}
          <GroupLabel>Applications</GroupLabel>

          <SidebarMenuItem>
            <SidebarMenuButton asChild active={pathname === '/'}>
              <Link to="/" onClick={dismissOnMobile}>
                <Icon icon={House} size="small" />
                <span className="truncate">Dashboard</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>

          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              active={pathname.startsWith(`/products/${FAST_FOOD_PRODUCT_KEY}`)}
            >
              <Link
                to="/products/$productKey/review"
                params={{ productKey: FAST_FOOD_PRODUCT_KEY }}
                onClick={dismissOnMobile}
              >
                <Icon icon={UtensilsCrossed} size="small" />
                <span className="truncate">
                  {fastFood?.label ?? 'UK Fast Food Menus'}
                </span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>

          {/*
            The current application's two destinations, nested under it: the Review
            queue and the Menus list. Categories are not here — they belong to a
            Restaurant, reached under Menus → a Restaurant → Categories, so the sidebar
            lists the Product's two top-level destinations and no deeper. Indented so the
            drill-in reads as belonging to the application above, inline in the one
            sidebar. `min-w-0` on the nested list and `truncate` on each label mean a
            long label is clipped rather than widening the rail — the rail never scrolls
            sideways (`overflow-x-hidden` on the column seals it).
          */}
          <li className="w-full min-w-0 pl-hurdl-4">
            <SidebarMenu className="min-w-0 border-l border-border pl-hurdl-2">
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  active={
                    pathname.startsWith(`${base}/review`) &&
                    !pathname.includes('/menus/')
                  }
                >
                  <Link
                    to="/products/$productKey/review"
                    params={{ productKey: FAST_FOOD_PRODUCT_KEY }}
                    onClick={dismissOnMobile}
                  >
                    <Icon icon={ClipboardList} size="small" />
                    <span className="truncate">Review queue</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  active={pathname.startsWith(`${base}/menus`)}
                >
                  <Link
                    to="/products/$productKey/menus"
                    params={{ productKey: FAST_FOOD_PRODUCT_KEY }}
                    onClick={dismissOnMobile}
                  >
                    <Icon icon={UtensilsCrossed} size="small" />
                    <span className="truncate">Restaurants</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </li>

          {/* Admin group — the panel's own administration. */}
          <GroupLabel>Admin</GroupLabel>

          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              active={pathname.startsWith('/admin/users')}
            >
              <Link to="/admin/users" onClick={dismissOnMobile}>
                <Icon icon={Users} size="small" />
                <span className="truncate">Users</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>

          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              active={pathname.startsWith('/admin/settings')}
            >
              <Link to="/admin/settings" onClick={dismissOnMobile}>
                <Icon icon={Settings} size="small" />
                <span className="truncate">Settings</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarContent>
    </Sidebar>
  );
}

/** A group heading in the sidebar: a small uppercase caption, non-interactive. */
function GroupLabel({
  children,
}: {
  readonly children: ReactNode;
}): ReactElement {
  return (
    <li className="px-hurdl-3 pb-hurdl-1 pt-hurdl-3 first:pt-hurdl-1">
      <span className="text-hurdl-caption font-hurdl-semibold uppercase tracking-wide text-muted-foreground">
        {children}
      </span>
    </li>
  );
}
